import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend, LabelList,
} from 'recharts';
import { inr } from './constants';

const STAGE_COLORS = {
  demos: '#94a3b8',
  demo_done: '#6366f1',
  activation: '#ec4899',
  won: '#22c55e',
};
const STAGE_LABELS = {
  demos: 'Demos',
  demo_done: 'Demo Done',
  activation: 'Activation',
  won: 'Won',
};
const PIE_COLORS = ['#22c55e', '#6366f1', '#ec4899', '#f59e0b', '#06b6d4', '#a855f7'];

const StageTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-bold text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-1.5" style={{ color: p.color }}>
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color }} />
          {STAGE_LABELS[p.dataKey]}: <b>{p.value}</b>
        </p>
      ))}
    </div>
  );
};

const RevenueTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-bold text-slate-700">{d.label}</p>
      <p className="text-green-700 font-semibold">{inr(d.revenue)}</p>
      <p className="text-slate-500">{d.won} schools won</p>
    </div>
  );
};

export default function DemoFunnelCharts({ funnel }) {
  if (!funnel || !funnel.sources || !funnel.totals || funnel.totals.demos === 0) return null;

  const barData = funnel.sources.map((s) => ({
    label: s.label,
    demos: s.demos,
    demo_done: s.demo_done,
    activation: s.activation,
    won: s.won,
  }));

  const revenueData = funnel.sources
    .filter((s) => (s.revenue || 0) > 0)
    .map((s) => ({ label: s.label, revenue: s.revenue, won: s.won }));

  const t = funnel.totals;
  const demoToWon = t.demos > 0 ? Math.round((t.won / t.demos) * 100) : 0;
  const demoToActivation = t.demos > 0 ? Math.round((t.activation / t.demos) * 100) : 0;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5" data-testid="crm-demo-funnel-charts">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-sm font-bold text-slate-700">🎬 Demo Source Attribution</p>
          <span className="text-[11px] text-slate-400">कौन से channel से सबसे ज़्यादा demo → activation → payment आ रहा है</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="bg-indigo-50 rounded-lg px-3 py-1.5 text-center" data-testid="funnel-kpi-demos">
            <p className="text-[10px] text-indigo-400 uppercase tracking-wide">Total Demos</p>
            <p className="text-lg font-bold text-indigo-700 leading-tight">{t.demos}</p>
          </div>
          <div className="bg-pink-50 rounded-lg px-3 py-1.5 text-center" data-testid="funnel-kpi-activation">
            <p className="text-[10px] text-pink-400 uppercase tracking-wide">Demo→Activation</p>
            <p className="text-lg font-bold text-pink-700 leading-tight">{demoToActivation}%</p>
          </div>
          <div className="bg-green-50 rounded-lg px-3 py-1.5 text-center" data-testid="funnel-kpi-conv">
            <p className="text-[10px] text-green-500 uppercase tracking-wide">Demo→Won</p>
            <p className="text-lg font-bold text-green-700 leading-tight">{demoToWon}%</p>
          </div>
          <div className="bg-emerald-50 rounded-lg px-3 py-1.5 text-center" data-testid="funnel-kpi-revenue">
            <p className="text-[10px] text-emerald-500 uppercase tracking-wide">Revenue</p>
            <p className="text-lg font-bold text-emerald-700 leading-tight">{inr(t.revenue)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Funnel grouped bar chart */}
        <div className="lg:col-span-2">
          <p className="text-xs font-semibold text-slate-500 mb-2">Funnel by Source</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={barData} barGap={2} barCategoryGap="22%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip content={<StageTooltip />} cursor={{ fill: '#f8fafc' }} />
              <Legend
                iconType="circle"
                wrapperStyle={{ fontSize: 11 }}
                formatter={(v) => STAGE_LABELS[v] || v}
              />
              {Object.keys(STAGE_COLORS).map((k) => (
                <Bar key={k} dataKey={k} fill={STAGE_COLORS[k]} radius={[3, 3, 0, 0]} maxBarSize={26}>
                  {k === 'won' && <LabelList dataKey="won" position="top" fontSize={10} fill="#16a34a" />}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue donut */}
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-2">Revenue by Source</p>
          {revenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={revenueData}
                  dataKey="revenue"
                  nameKey="label"
                  cx="50%"
                  cy="45%"
                  innerRadius={48}
                  outerRadius={82}
                  paddingAngle={2}
                >
                  {revenueData.map((entry, i) => (
                    <Cell key={entry.label} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<RevenueTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex flex-col items-center justify-center text-center text-slate-400" data-testid="funnel-revenue-empty">
              <p className="text-3xl mb-2">💰</p>
              <p className="text-xs">अभी किसी source से payment नहीं हुआ है</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
